// BuilderGuideBook - 画面ごとの機能をまとめて見せる手引き（章 + ページ送り）
//
// スポットライト（GuideOverlay）は「そのボタンはどこ」を指す機能ごとのヘルプ。
// こちらは画面全体で何ができるかを、実際の画面キャプチャとともに順に見せる。
//
// 1画面につきキャプチャは1枚。ページごとに focus（キャプチャに対する比率）を持ち、
// 丸で囲む（HIGHLIGHT）か、そこだけ拡大する（ZOOM）かを切り替える。
// そのため説明を増やしても画像は増えない。囲みの見た目は GuideOverlay と揃える。
//
// キャプチャは言語ごとに用意する。命名は既存の display_bg_course_{locale} と同じ作法で、
// ja / ja_kanji はそれぞれ別画像（ひらがなと漢字で UI の文字が変わるため）。
//   assets/images/builder_guide/{book}_{capture}_{locale}.webp

import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { btnDelay, usePlaySound } from "common/utils";
import { InScreenModal } from "./ScreenModal";
import { BuilderModalPanel, BuilderModalAccent } from "./BuilderModalPanel";

/** 手引きの種類。キャプチャのファイル名にも使う */
export enum GuideBookId {
  TOP = "top",
  EDITOR = "editor",
}

/** キャプチャの見せ方 */
export enum GuideFocusMode {
  /** 画面全体 */
  FULL = "full",
  /** 全体を見せて、該当箇所だけ明るく残して囲む */
  HIGHLIGHT = "highlight",
  /** 該当箇所を拡大して囲む */
  ZOOM = "zoom",
}

/** キャプチャに対する比率（0〜1） */
export type GuideRect = { x: number; y: number; w: number; h: number };

export type GuideBookPage = {
  key: string;
  /** キャプチャのファイル名に使う識別子 */
  capture: string;
  titleKey: string;
  descKey: string;
  mode: GuideFocusMode;
  focus?: GuideRect;
};

export type GuideBookChapter = {
  key: string;
  labelKey: string;
  icon: string;
  pages: GuideBookPage[];
};

// 1124x572 のゲーム機の枠に収める必要がある。キャプチャと説明を横に並べ、
// キャプチャの高さを固定して全体の高さが決まるようにする。
const PANEL_WIDTH = 1000;
const CAPTURE_WIDTH = 560;
const CAPTURE_HEIGHT = 300;
// 囲みが対象に張り付かないよう、キャプチャ上で少し広げる
const FOCUS_PADDING = 6;
// 拡大したときに囲みが枠の端で切れないよう、内側に取る余白
const ZOOM_INSET = FOCUS_PADDING + 10;
// キャプチャを実寸より引き伸ばすと滲む。拡大はここで打ち止めにする。
// 小さいボタンのページは枠いっぱいにならないが、そのぶん くっきり見える
const MAX_ZOOM_SCALE = 1;
// 暗幕の色（Tailwind の色トークンに black が無いため inline で指定する）
const SCRIM = "rgba(0,0,0,0.6)";

const captureUrl = (book: GuideBookId, capture: string, locale: string) =>
  `${Constants.assetHost}/assets/images/builder_guide/${book}_${capture}_${locale}.webp`;

type Box = { left: number; top: number; width: number; height: number };

/**
 * キャプチャを枠に収めたときの、枠内での画像の位置と大きさ。
 * 画面のはしを囲むときに囲みの線が枠で切れるので、囲みのぶんだけ内側に収める。
 */
const containedImage = (natural: { w: number; h: number }): Box => {
  const scale = Math.min(
    (CAPTURE_WIDTH - FOCUS_PADDING * 2) / natural.w,
    (CAPTURE_HEIGHT - FOCUS_PADDING * 2) / natural.h,
  );
  const width = natural.w * scale;
  const height = natural.h * scale;
  return {
    left: (CAPTURE_WIDTH - width) / 2,
    top: (CAPTURE_HEIGHT - height) / 2,
    width,
    height,
  };
};

/**
 * focus を枠の中央に置くと、端の方を指しているときに枠の外側が余る。
 * 画像で枠を埋めるように寄せる（ZOOM_INSET のぶんだけは余ってよい）。
 * 画像が枠より小さいときは埋められないので、そのまま中央に置く。
 */
const place = (want: number, size: number, box: number) => {
  const min = box - ZOOM_INSET - size;
  const max = ZOOM_INSET;
  return min <= max ? Math.min(Math.max(want, min), max) : (box - size) / 2;
};

/**
 * focus が枠いっぱいに入るまで拡大したときの、枠内での画像の位置と大きさ。
 * 横長すぎる／縦長すぎる focus をそのまま収めると枠の上下（左右）が余るので、
 * 枠と同じ比率になるまで focus のまわりを広げてから収める。囲みは元の focus のまま。
 */
const zoomedImage = (natural: { w: number; h: number }, focus: GuideRect): Box => {
  const innerW = CAPTURE_WIDTH - ZOOM_INSET * 2;
  const innerH = CAPTURE_HEIGHT - ZOOM_INSET * 2;
  const focusW = focus.w * natural.w;
  const focusH = focus.h * natural.h;
  const area =
    focusW / focusH > innerW / innerH
      ? { w: focusW, h: (focusW * innerH) / innerW }
      : { w: (focusH * innerW) / innerH, h: focusH };
  const scale = Math.min(innerW / area.w, innerH / area.h, MAX_ZOOM_SCALE);
  const width = natural.w * scale;
  const height = natural.h * scale;
  return {
    left: place(CAPTURE_WIDTH / 2 - (focus.x + focus.w / 2) * width, width, CAPTURE_WIDTH),
    top: place(CAPTURE_HEIGHT / 2 - (focus.y + focus.h / 2) * height, height, CAPTURE_HEIGHT),
    width,
    height,
  };
};

/** 囲みの位置（枠内の座標）。対象に少し余白を足す */
const focusBox = (image: Box, focus: GuideRect): Box => ({
  left: image.left + focus.x * image.width - FOCUS_PADDING,
  top: image.top + focus.y * image.height - FOCUS_PADDING,
  width: focus.w * image.width + FOCUS_PADDING * 2,
  height: focus.h * image.height + FOCUS_PADDING * 2,
});

/** 囲みの外を暗くする4枚の帯。clip-path の穴あけは事故りやすいので使わない */
const Scrim = ({ box }: { box: Box }) => (
  <>
    <div className="absolute left-0 right-0 top-0" style={{ height: box.top, background: SCRIM }} />
    <div
      className="absolute left-0 right-0 bottom-0"
      style={{ top: box.top + box.height, background: SCRIM }}
    />
    <div
      className="absolute left-0"
      style={{ top: box.top, width: box.left, height: box.height, background: SCRIM }}
    />
    <div
      className="absolute right-0"
      style={{ top: box.top, left: box.left + box.width, height: box.height, background: SCRIM }}
    />
  </>
);

const CaptureView = ({
  book,
  page,
  locale,
}: {
  book: GuideBookId;
  page: GuideBookPage;
  locale: string;
}) => {
  // キャプチャが未配置の言語でも画像割れを見せない
  const [missing, setMissing] = useState(false);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  const src = captureUrl(book, page.capture, locale);
  const zoom = page.mode === GuideFocusMode.ZOOM && page.focus;
  const image = natural
    ? zoom
      ? zoomedImage(natural, page.focus!)
      : containedImage(natural)
    : null;
  const box = image && page.focus ? focusBox(image, page.focus) : null;

  return (
    <div
      style={{ width: CAPTURE_WIDTH, height: CAPTURE_HEIGHT }}
      className="flex-col-el flex-center shrink-0 relative rounded-xl bg-gray-10 border-solid border-2 border-gray2-30 overflow-hidden"
    >
      {missing ? (
        <span className="material-symbols-outlined type-display text-gray2-40">image</span>
      ) : (
        <>
          <img
            key={src}
            alt={I18n.t(page.titleKey)}
            src={src}
            onLoad={(e) =>
              setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
            }
            onError={() => setMissing(true)}
            className="absolute max-w-none"
            style={image ?? { opacity: 0 }}
          />

          {box && page.mode !== GuideFocusMode.FULL && (
            <>
              {page.mode === GuideFocusMode.HIGHLIGHT && <Scrim box={box} />}
              <div
                className="absolute box-border rounded-lg border-solid border-4 border-yellow-100 pointer-events-none"
                style={box}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export const BuilderGuideBook = ({
  open,
  book,
  chapters,
  onClose,
  onReplayGuide,
}: {
  open: boolean;
  book: GuideBookId;
  chapters: GuideBookChapter[];
  onClose: () => void;
  /** 指定時のみ、初回のスポットライトをもう一度出すボタンを見出しに置く */
  onReplayGuide?: () => void;
}) => {
  const play = usePlaySound();
  const locale = useSelector((state: RootState) => state.config.userConfig.locale) ?? "ja";
  const [index, setIndex] = useState(0);

  // ページ送りは章をまたいで一本道。章タブは現在地の章に追従する
  const flat = useMemo(
    () => chapters.flatMap((c, ci) => c.pages.map((p, pi) => ({ chapter: ci, page: pi, ...p }))),
    [chapters],
  );
  const current = flat[Math.min(index, flat.length - 1)];
  const chapter = chapters[current.chapter];
  const isFirst = index === 0;
  const isLast = index >= flat.length - 1;

  const handleClose = () => {
    play();
    btnDelay(() => {
      setIndex(0);
      onClose();
    });
  };

  const handlePrev = () => {
    if (isFirst) return;
    play();
    btnDelay(() => setIndex((i) => i - 1));
  };

  const handleNext = () => {
    play();
    btnDelay(() => (isLast ? handleClose() : setIndex((i) => i + 1)));
  };

  const handleReplayGuide = () => {
    play();
    btnDelay(() => {
      setIndex(0);
      onReplayGuide?.();
    });
  };

  const handleChapter = (target: number) => {
    play();
    btnDelay(() => setIndex(flat.findIndex((p) => p.chapter === target)));
  };

  return (
    <InScreenModal open={open} onBackdropClose={handleClose}>
      <BuilderModalPanel
        accent={BuilderModalAccent.BLUE}
        icon="menu_book"
        title={I18n.t(chapters.length > 1 ? "MSG_BUILDER_GUIDEBOOK_TITLE" : chapter.labelKey)}
        width={PANEL_WIDTH}
        onClose={handleClose}
        headerAction={
          onReplayGuide && (
            <button
              type="button"
              onClick={handleReplayGuide}
              className="type-inherit-font flex-row-el flex-center gap-1 shrink-0 h-9 px-3 rounded-full bg-blue-10 border-2 border-blue-60 cursor-pointer whitespace-nowrap"
            >
              <span className="material-symbols-outlined type-sm text-blue-100">tour</span>
              <span className="type-sm text-textcolor-black">
                {I18n.t("MSG_BUILDER_GUIDE_REPLAY")}
              </span>
            </button>
          )
        }
      >
        {/* 章タブ。位置は固定で、中身が変わっても動かさない。章が1つなら出さない */}
        {chapters.length > 1 && (
          <div className="flex-row-el items-stretch w-full gap-2">
            {chapters.map((c, i) => {
              const active = i === current.chapter;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => handleChapter(i)}
                  className={`type-inherit-font flex-row-el flex-center flex-1 min-w-0 gap-1 h-9 px-2 rounded-full border-solid border-2 cursor-pointer ${
                    active ? "bg-blue-10 border-blue-100" : "bg-white border-gray2-30"
                  }`}
                >
                  <span
                    className={`material-symbols-outlined type-sm ${
                      active ? "text-blue-100" : "text-gray2-100"
                    }`}
                  >
                    {c.icon}
                  </span>
                  <span className="type-sm text-textcolor-black truncate">
                    {I18n.t(c.labelKey)}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex-row-el items-start w-full gap-4">
          <CaptureView key={current.key} book={book} page={current} locale={locale} />

          <div className="flex-col-el items-start flex-1 min-w-0 gap-2">
            <p className="type-text-left type-sm text-blue-100 w-full">
              {I18n.t(chapter.labelKey)}
            </p>
            <p className="type-text-left type-md text-textcolor-black w-full">
              {I18n.t(current.titleKey)}
            </p>
            {/* 説明は箇条書き。長い文章は読まれないので、1行ずつ短く書く */}
            <div className="flex-col-el items-start w-full gap-1">
              {I18n.t(current.descKey)
                .split("\n")
                .map((line) => (
                  <div key={line} className="flex-row-el items-start w-full gap-2">
                    <span className="shrink-0 type-body text-blue-100">・</span>
                    <p className="type-text-left type-body text-textcolor-black/70 flex-1 min-w-0">
                      {line}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className="flex-row-el items-stretch justify-between w-full gap-2 upsilon">
          <button
            type="button"
            onClick={handlePrev}
            disabled={isFirst}
            className="type-inherit-font btn btn-blue shrink-0 disabled:opacity-40"
          >
            <div className="flex-row-center gap-1">
              <span className="material-symbols-outlined text-white type-md">chevron_left</span>
              <p className="type-text type-body text-white whitespace-nowrap">
                {I18n.t("MSG_BUILDER_GUIDEBOOK_PREV")}
              </p>
            </div>
          </button>

          <span className="type-text type-sm text-textcolor-black/50 self-center whitespace-nowrap">
            {I18n.t("MSG_BUILDER_GUIDEBOOK_PAGE", {
              current: current.page + 1,
              total: chapter.pages.length,
            })}
          </span>

          <button
            type="button"
            onClick={handleNext}
            className="type-inherit-font btn btn-primary shrink-0"
          >
            <div className="flex-row-center gap-1">
              <span className="material-symbols-outlined text-white type-md">
                {isLast ? "check" : "chevron_right"}
              </span>
              <p className="type-text type-body text-white whitespace-nowrap">
                {I18n.t(isLast ? "MSG_BUILDER_GUIDEBOOK_DONE" : "MSG_BUILDER_GUIDEBOOK_NEXT")}
              </p>
            </div>
          </button>
        </div>
      </BuilderModalPanel>
    </InScreenModal>
  );
};
